import { createFileRoute } from "@tanstack/react-router";
import { Bullets, LegalPage, Note, Section, Value } from "@/components/legal-page";
import { LEGAL, NEVER_DO } from "@/lib/legal";

export const Route = createFileRoute("/data-use")({
  head: () => ({
    meta: [
      { title: "Xamseen — How we use data" },
      {
        name: "description",
        content:
          "The plain-language version: what Xamseen collects, what we never do with it, how location precision works, and the controls you hold.",
      },
      { property: "og:title", content: "Xamseen — How we use data" },
      { property: "og:description", content: "Data use at Xamseen, in plain language, on one page." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://xamseen.com/data-use" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://xamseen.com/data-use" }],
  }),
  component: DataUsePage,
});

const TOC = [
  { id: "principles", label: "Our principles" },
  { id: "location", label: "How location works" },
  { id: "media", label: "Photos, video and your camera" },
  { id: "insight", label: "Turning reports into patterns" },
  { id: "never", label: "What we never do" },
  { id: "controls", label: "Controls you hold" },
];

function DataUsePage() {
  return (
    <LegalPage
      title="How Xamseen uses data"
      intro="This is the plain-language companion to our Privacy Policy. Same commitments, less legal phrasing. If the two ever seem to disagree, the Privacy Policy is the authoritative version."
      toc={TOC}
    >
      <Section id="principles" title="Our principles">
        <Bullets
          items={[
            <>
              <strong>You choose what to share.</strong> Xamseen collects almost nothing you did not deliberately submit.
              A blank profile and a report with no coordinates are both perfectly valid ways to use it.
            </>,
            <>
              <strong>Minimum viable precision.</strong> Where a rough answer does the job, we store the rough answer.
              This is why location precision is a setting rather than an assumption.
            </>,
            <>
              <strong>Patterns over people.</strong> The value of Xamseen is in the aggregate — five reports of the same
              broken crossing matter more than any one of them. We prefer aggregated and de-identified data whenever
              identifying a person is not necessary.
            </>,
            <>
              <strong>No advertising business.</strong> We do not fund Xamseen by selling access to you, so we have no
              reason to collect data we do not need.
            </>,
            <>
              <strong>Say it plainly.</strong> Where something is not yet decided or not yet reviewed, we mark it as
              pending instead of writing a confident sentence we cannot back up.
            </>,
          ]}
        />
      </Section>

      <Section id="location" title="How location works">
        <p>
          Location is never taken silently. Your browser asks for permission, and Xamseen only requests it at the moment
          you use a feature that needs it — attaching a place to a report, or centring the map on you. You can decline and
          still file a report by describing the place in words.
        </p>
        <p>
          When you do attach a location, the precision Xamseen stores is up to you. Settings → Privacy &amp; your data →
          Location precision offers:
        </p>
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-semibold">Setting</th>
                <th className="px-4 py-3 font-semibold">What gets stored</th>
                <th className="px-4 py-3 font-semibold">Roughly</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border align-top">
                <td className="px-4 py-3 font-medium">Exact</td>
                <td className="px-4 py-3 text-foreground/80">The precise coordinates you confirmed.</td>
                <td className="px-4 py-3 text-muted-foreground">Street address level</td>
              </tr>
              <tr className="border-t border-border align-top">
                <td className="px-4 py-3 font-medium">Approximate</td>
                <td className="px-4 py-3 text-foreground/80">Coordinates rounded before they are saved.</td>
                <td className="px-4 py-3 text-muted-foreground">Neighbourhood, ~1 km</td>
              </tr>
              <tr className="border-t border-border align-top">
                <td className="px-4 py-3 font-medium">City only</td>
                <td className="px-4 py-3 text-foreground/80">Coordinates rounded hard, so only the settlement remains.</td>
                <td className="px-4 py-3 text-muted-foreground">City, ~10 km</td>
              </tr>
              <tr className="border-t border-border align-top">
                <td className="px-4 py-3 font-medium">Do not store</td>
                <td className="px-4 py-3 text-foreground/80">No coordinates are saved with your reports at all.</td>
                <td className="px-4 py-3 text-muted-foreground">Nothing</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Rounding is applied on our servers when the report is saved, so the reduced value is what gets stored — not just
          what gets displayed. Changing the setting affects new reports; existing reports keep the precision they were
          saved with, and you can delete any report at any time.
        </p>
        <Note>
          Xamseen does not track your location in the background, does not build a location history of your movements, and
          does not store a location for you when you are simply browsing the app.
        </Note>
      </Section>

      <Section id="media" title="Photos, video and your camera">
        <p>
          Camera access is requested only when you tap to take a photo or video for a report. Xamseen does not access your
          camera or microphone at any other time, and there is no background capture.
        </p>
        <p>
          Photos can contain more than you intend — a face in the background, a licence plate, a house number, or embedded
          location data from your camera. Before you submit, take a moment to check what is actually in the frame. Blur or
          crop anything that identifies a person who has not agreed to be part of your report.
        </p>
      </Section>

      <Section id="insight" title="Turning reports into patterns">
        <p>
          Xamseen groups reports by area, category and recurrence to show where problems concentrate and which ones keep
          coming back. That analysis works on the reports themselves, not on profiles of the people who filed them.
        </p>
        <p>
          Automated assessment reads the text and photos of a submitted report to suggest a category, a severity and a
          likely responsible authority. It is a routing aid: it can be wrong, it is not advice, and it is not a decision by
          any authority. A human — you, your neighbours, or the responsible organisation — remains the one who judges.
        </p>
      </Section>

      <Section id="never" title="What we never do">
        <Bullets items={NEVER_DO.map((n) => <>{n}</>)} />
      </Section>

      <Section id="controls" title="Controls you hold">
        <Bullets
          items={[
            <>
              <strong>Export everything.</strong> Settings → Privacy &amp; your data → Download my data gives you a JSON
              file with your profile, posts, comments, reports and preferences.
            </>,
            <>
              <strong>Delete everything.</strong> Same panel: Delete my account removes your account, profile and content.
              It cannot be undone.
            </>,
            <>
              <strong>Set location precision.</strong> Including refusing to store coordinates at all.
            </>,
            <>
              <strong>Set profile visibility.</strong> Choose whether your profile is public, visible to signed-in members,
              or private and excluded from search and discovery.
            </>,
            <>
              <strong>Choose your notifications.</strong> Per-category in-app and email switches. Security and account
              emails always send, because they protect your account.
            </>,
          ]}
        />
        <p>
          Anything you cannot do yourself, ask us:{" "}
          <Value value={LEGAL.privacyContactEmail} label="privacy contact email address" />. Full detail lives in the{" "}
          <a className="text-plum hover:underline" href="/privacy">
            Privacy Policy
          </a>
          .
        </p>
      </Section>
    </LegalPage>
  );
}
